/**
 * Core Backgammon Game Types
 *
 * Board: 24 points + bar (0, 25) represented as array of 26 positions.
 * Positive numbers = Player 1 checkers, negative = Player 2 checkers.
 * Player 1 moves from point 24 → 1 (home board 1-6).
 * Player 2 moves from point 1 → 24 (home board 19-24).
 */

// ============================================================================
// Board Configuration
// ============================================================================

/** Player side: player1 moves toward point 1, player2 moves toward point 24 */
export type PlayerSide = 'player1' | 'player2';

/** Checker color for display */
export type CheckerColor = 'dark' | 'light';

/**
 * Board representation: array of 26 integers.
 * Index 0 = Player 1 bar
 * Index 1-24 = Points 1-24
 * Index 25 = Player 2 bar
 * Positive = Player 1 checkers, Negative = Player 2 checkers
 */
export type BackgammonBoard = number[];

/** A single die value (1-6) */
export type DieValue = 1 | 2 | 3 | 4 | 5 | 6;

/** Dice roll result */
export interface DiceRoll {
  die1: DieValue;
  die2: DieValue;
  isDoubles: boolean;
  /** Available moves: 2 values normally, 4 for doubles */
  movesRemaining: DieValue[];
}

/** A single move: from one point to another */
export interface BackgammonMove {
  from: number; // 0 = P1 bar, 1-24 = point, 25 = P2 bar
  to: number;   // 1-24 = point, 0 = bear off P1, 25 = bear off P2
  dieValue: DieValue;
  isHit: boolean;
  isBearOff: boolean;
  isBarEntry: boolean;
}

/** Result of executing a move */
export interface MoveResult {
  move: BackgammonMove;
  newBoard: BackgammonBoard;
  capturedToBar: boolean;
}

// ============================================================================
// Game State
// ============================================================================

export interface BackgammonGameState {
  board: BackgammonBoard;
  currentPlayer: PlayerSide;
  dice: DiceRoll | null;
  moveHistory: BackgammonMove[][];
  gameStatus: 'waiting' | 'rolling' | 'moving' | 'completed';
  winner?: PlayerSide;
  borneOff: { player1: number; player2: number };
  turnNumber: number;
  gameId: string;
  startedAt: number;
  selectedPoint: number | null;
  validMoves: BackgammonMove[];
}

// ============================================================================
// Game Settings
// ============================================================================

export type BackgammonDifficulty = 'easy' | 'medium' | 'hard';

export interface BackgammonGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  aiDifficulty?: BackgammonDifficulty;
  playerSide?: PlayerSide;
}

// ============================================================================
// Statistics
// ============================================================================

export interface BackgammonGameStats {
  wins: number;
  losses: number;
  draws: number;
  winPercentage: number;
  averageMoves: number;
  totalCheckersHit: number;
  totalGammons: number;
}

export interface BackgammonStatistics {
  userId?: string;
  singlePlayer: {
    easy: BackgammonGameStats;
    medium: BackgammonGameStats;
    hard: BackgammonGameStats;
  };
  localMultiplayer: BackgammonGameStats;
  onlineMultiplayer: BackgammonGameStats;
  totalGames: number;
  totalPlayTime: number;
}

export interface BackgammonGameResult {
  gameId: string;
  mode: BackgammonGameSettings['mode'];
  difficulty?: BackgammonDifficulty;
  winner?: PlayerSide;
  totalMoves: number;
  totalHits: number;
  isGammon: boolean;
  duration: number;
  completedAt: number;
}

// ============================================================================
// Component Props
// ============================================================================

export interface BackgammonGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

export interface BackgammonGameModeSelectorProps {
  onStartGame: (settings: BackgammonGameSettings) => void;
  onJoinGame?: (roomCode: string) => Promise<void>;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
  isOnline?: boolean;
}

export interface BackgammonBoardProps {
  board: BackgammonBoard;
  currentPlayer: PlayerSide;
  dice: DiceRoll | null;
  validMoves: BackgammonMove[];
  selectedPoint: number | null;
  borneOff: { player1: number; player2: number };
  onPointClick: (point: number) => void;
  onBearOff: () => void;
  disabled?: boolean;
  gameStatus: BackgammonGameState['gameStatus'];
}

// ============================================================================
// Constants
// ============================================================================

/** Total number of checkers per player */
export const CHECKERS_PER_PLAYER = 15;

/** Number of points on the board */
export const NUM_POINTS = 24;

/** Player 1 bar index */
export const P1_BAR = 0;

/** Player 2 bar index */
export const P2_BAR = 25;

/**
 * Initial board setup.
 * Index 0 = P1 bar, 1-24 = points, 25 = P2 bar.
 * Positive = P1, Negative = P2.
 */
export const INITIAL_BOARD: BackgammonBoard = [
  0,    // 0: P1 bar
  0,    // 1
  0,    // 2
  0,    // 3
  0,    // 4
  0,    // 5
  5,    // 6: 5 P1 checkers
  0,    // 7
  3,    // 8: 3 P1 checkers
  0,    // 9
  0,    // 10
  0,    // 11
  -5,   // 12: 5 P2 checkers
  5,    // 13: 5 P1 checkers (note: was listed as point 13)
  0,    // 14
  0,    // 15
  0,    // 16
  -3,   // 17: 3 P2 checkers
  0,    // 18
  -5,   // 19: 5 P2 checkers
  0,    // 20
  0,    // 21
  0,    // 22
  0,    // 23
  -2,   // 24: 2 P2 checkers
  0,    // 25: P2 bar
];
